export const UserProperties = {
  USER_ID: 'user_id',
  SIGNUP_DATE: 'signup_date',
  CONVERSION_STAGE: 'conversion_stage',
  CAMPAIGNS_GENERATED: 'campaigns_generated',
  SIGNUP_SOURCE: 'signup_source',
  LAST_ACTIVE_DATE: 'last_active_date',
  BILLING_CYCLE: 'billing_cycle',
};

export const ConversionStage = {
  ADVANCE_ANNUAL: 'Advance(annual)',
  ADVANCE_LIFETIME: 'Advance(lifetime)',
  ADVANCE_MONTHLY: 'Advance(monthly)',
  BEGIN_ANNUAL: 'Begin(annual)',
  BEGIN_LIFETIME: 'Begin(lifetime)',
  BEGIN_MONTHLY: 'Begin(monthly)',
  CONQUER_ANNUAL: 'Conquer(annual)',
  CONQUER_LIFETIME: 'Conquer(lifetime)',
  CONQUER_MONTHLY: 'Conquer(monthly)',
  FREE: 'Free',
  NOT_REGISTERED: 'Not registered',
} as const;
export type ConversionStageType =
  (typeof ConversionStage)[keyof typeof ConversionStage];

export const BillingCycle = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  LIFETIME: 'lifetime',
} as const;
export type BillingCycleType = (typeof BillingCycle)[keyof typeof BillingCycle];

export const SocialPlatform = {
  LINKEDIN: 'linkedin',
  TWITTER: 'twitter',
  FACEBOOK: 'facebook',
} as const;
export type SocialPlatformType = (typeof SocialPlatform)[keyof typeof SocialPlatform];

export const AnalyticsEvents = {
  SIGN_UP: {
    name: 'sign_up',
    // properties: method ('google'), user_id, category ('campaigns_list_screen', 'campaign_screen', 'profile_screen', 'register_screen', or 'fallback')
    properties: {
      METHOD: 'method',
      USER_ID: UserProperties.USER_ID,
      CATEGORY: 'category',
    },
    values: {
      METHOD: {
        GOOGLE: 'google',
      },
      CATEGORY: {
        CAMPAIGNS_LIST_SCREEN: 'campaigns_list_screen',
        CAMPAIGN_SCREEN: 'campaign_screen',
        PROFILE_SCREEN: 'profile_screen',
        REGISTER_SCREEN: 'register_screen',
        FALLBACK: 'fallback', // For cases where the screen is not tracked
      },
    },
  },
  CLICK_PRIVACY_POLICY: {
    name: 'click_privacy_policy',
    category: 'footer',
  },
  CLICK_TERMS_OF_SERVICE: {
    name: 'click_terms_of_service',
    category: 'footer',
  },
  CLICK_COOKIE_POLICY: {
    name: 'click_cookie_policy',
    category: 'footer',
  },
  CLICK_PROFILE: {
    name: 'click_profile',
    // No category - event tracked without category context
  },
  SHOW_TOOLTIP_MANUAL_MODE: {
    name: 'show_tooltip_manual_mode',
    // No category - event tracked without category context
  },
  CLICK_LOGOUT: {
    name: 'click_logout',
    category: 'profile',
  },
  SHOW_SUPPORT_CHAT: {
    name: 'show_support_chat',
    // properties: user_id
    category: 'crisp',
    properties: {
      USER_ID: UserProperties.USER_ID,
    },
  },
  CLICK_SOCIAL: {
    name: 'click_social',
    // label: 'linkedin', 'twitter', or 'facebook'
    category: 'header',
    label: {
      LINKEDIN: SocialPlatform.LINKEDIN,
      TWITTER: SocialPlatform.TWITTER,
      FACEBOOK: SocialPlatform.FACEBOOK,
    },
  },
  SIGN_OUT: {
    name: 'sign_out',
    // category: 'header' or 'profile_screen' (provided dynamically)
    // properties: user_id
    properties: {
      USER_ID: UserProperties.USER_ID,
    },
  },
  STRIPE_PURCHASE_SUCCESSFUL_3: {
    name: 'stripe_purchase_successful_3',
    // properties: checkout_session_id, user_id, amount, currency, plan_name, plan_code, subscription_id, customer_id, purchase_number, is_upgrade, previous_plan, payment_method
    category: 'payments',
    properties: {
      CHECKOUT_SESSION_ID: 'checkout_session_id',
      USER_ID: UserProperties.USER_ID,
      AMOUNT: 'amount',
      CURRENCY: 'currency',
      PLAN_NAME: 'plan_name',
      PLAN_CODE: 'plan_code',
      SUBSCRIPTION_ID: 'subscription_id',
      CUSTOMER_ID: 'customer_id',
      PURCHASE_NUMBER: 'purchase_number',
      IS_UPGRADE: 'is_upgrade',
      PREVIOUS_PLAN: 'previous_plan',
      PAYMENT_METHOD: 'payment_method',
    },
  },
  CLICK_BUTTON_CREDITS: {
    name: 'click_button_credits',
    category: 'header',
  },
  CLICK_BUTTON_NEW_CAMPAIGN: {
    name: 'click_button_new_campaign',
    // category: 'header' or 'campaigns' (provided dynamically)
  },
  CLICK_BUTTON_PROFILE: {
    name: 'click_button_profile',
    category: 'header',
  },
  CLICK_CAMPAIGN: {
    name: 'click_campaign',
    // No category - event tracked without category context
  },
  CLICK_CAMPAIGN_DELETE: {
    name: 'click_campaign_delete',
    // category: 'campaigns_list' or 'campaign' (provided dynamically)
  },
  CLICK_CAMPAIGN_RENAME: {
    name: 'click_campaign_rename',
    // category: 'campaigns_list' or 'campaign' (provided dynamically)
  },
  CLICK_CAMPAIGN_THREE_DOTS_MENU: {
    name: 'click_campaign_three_dots_menu',
    // category: 'campaigns_list' or 'campaign' (provided dynamically)
  },
  CLICK_GENERATE_TAB_CONFIRMATION: {
    name: 'click_generate_tab_confirmation',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_GENERATED_TAB: {
    name: 'click_generated_tab',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_VIDEO_GUIDE: {
    name: 'click_video_guide',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_ICON_PROFILE: {
    name: 'click_icon_profile',
    category: 'header',
  },
  CLICK_MANAGE_SUBSCRIPTION: {
    name: 'click_manage_subscription',
    category: 'profile_screen',
  },
  CLICK_MY_CAMPAIGNS: {
    name: 'click_my_campaigns',
    category: 'header',
  },
  CLICK_PROFILE_TAB: {
    name: 'click_profile_tab',
    category: 'profile_screen',
  },
  CLICK_SECTION: {
    name: 'click_section',
    // category: section_name (provided dynamically)
  },
  CLICK_COPY: {
    name: 'click_copy',
    // category: section_name, label: tab_name, properties: item_name (all provided dynamically)
  },
  CLICK_REGENERATE: {
    name: 'click_regenerate',
    // category: section_name, label: tab_name, properties: item_name, regenerations_count (all provided dynamically)
  },
  CLICK_REGENERATE_CONFIRM: {
    name: 'click_regenerate_confirm',
    // category: section_name, label: tab_name, value: credits_cost, properties: item_name, creativity_level, prompt, regenerations_count (all provided dynamically)
  },
  CLICK_BANNER_STRUCTURED_FB_CLOSE: {
    name: 'click_banner_structured_fb_close',
    category: 'legacy_migration',
  },
  CLICK_BANNER_STRUCTURED_FB_CTA: {
    name: 'click_banner_structured_fb_cta',
    category: 'legacy_migration',
  },
  CLICK_GENERATE_CAMPAIGN_CONFIRMATION: {
    name: 'click_generate_campaign_confirmation',
    // category: 'header' or 'campaigns' (provided dynamically)
  },
  CLICK_STRUCTURED_ITEM_DISCARD: {
    name: 'click_structured_item_discard',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_ITEM_GENERATE: {
    name: 'click_structured_item_generate',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_ITEM_GENERATE_CANCEL: {
    name: 'click_structured_item_generate_cancel',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_ITEM_GENERATE_CONFIRM: {
    name: 'click_structured_item_generate_confirm',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_GENERATE_ALL: {
    name: 'click_structured_generate_all',
    // category: section_name, label: tab_name, value: credits_price (all provided dynamically)
  },
  CLICK_STRUCTURED_GENERATE_ALL_CANCEL: {
    name: 'click_structured_generate_all_cancel',
    // category: section_name, label: tab_name, value: credits_price (all provided dynamically)
  },
  CLICK_STRUCTURED_GENERATE_ALL_CONFIRM: {
    name: 'click_structured_generate_all_confirm',
    // category: section_name, label: tab_name, value: credits_price (all provided dynamically)
  },
  CLICK_STRUCTURED_MORE_IDEAS: {
    name: 'click_structured_more_ideas',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_MORE_IDEAS_CANCEL: {
    name: 'click_structured_more_ideas_cancel',
    // category: section_name, label: tab_name (both provided dynamically)
  },
  CLICK_STRUCTURED_MORE_IDEAS_CONFIRM: {
    name: 'click_structured_more_ideas_confirm',
    // category: section_name, label: tab_name (both provided dynamically)
  },
};
